import { useState, useEffect, useRef } from "react";
import toast from "../../../../utils/toast";
import {
  getKycDocList,
  uploadKycDoc,
  deleteKycDoc,
} from "../../../../services/tenant/tenantServices";

export default function KycDocuments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const fileInputRef = useRef(null);
  const [pendingUpload, setPendingUpload] = useState(null); // { ownerDocId, coTenantId }

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const res = await getKycDocList();
      if (res.statusCode === 1) setData(res.data);
    } catch {
      toast.error("Failed to load documents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, []);

  const triggerUpload = (ownerDocId, coTenantId) => {
    setPendingUpload({ ownerDocId, coTenantId });
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingUpload) return;
    const { ownerDocId, coTenantId } = pendingUpload;
    setActionLoading(ownerDocId);
    try {
      const res = await uploadKycDoc(ownerDocId, coTenantId, file);
      if (res.statusCode === 1) {
        toast.success(res.message);
        fetchDocs();
      } else {
        toast.error(res.message);
      }
    } catch {
      toast.error("Upload failed.");
    } finally {
      setActionLoading(null);
      setPendingUpload(null);
      e.target.value = "";
    }
  };

  const handleDelete = async (tenantKYCDocId) => {
    setActionLoading(tenantKYCDocId);
    try {
      const res = await deleteKycDoc(tenantKYCDocId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        fetchDocs();
      } else {
        toast.error(res.message);
      }
    } catch {
      toast.error("Delete failed.");
    } finally {
      setActionLoading(null);
    }
  };

  const getBanner = (status) => {
    if (status === "approved")
      return {
        bg: "#F0FDF4",
        border: "#86EFAC",
        color: "#15803D",
        icon: "✅",
        title: "KYC Approved",
        sub: "All documents verified successfully.",
      };
    if (status === "rejected")
      return {
        bg: "#FEF2F2",
        border: "#FCA5A5",
        color: "#DC2626",
        icon: "❌",
        title: "KYC Rejected",
        sub: "Some documents were rejected. Please re-upload.",
      };
    if (status === "pending")
      return {
        bg: "#FFFBEB",
        border: "#FCD34D",
        color: "#92400E",
        icon: "⏳",
        title: "KYC Under Review",
        sub: "Documents submitted and being reviewed.",
      };
    return {
      bg: "#FFF7ED",
      border: "#FED7AA",
      color: "#C2410C",
      icon: "⚠️",
      title: "KYC Incomplete",
      sub: "Some documents are still pending upload.",
    };
  };

  const getStatusStyle = (status) => {
    if (status === "approved")
      return { bg: "#DCFCE7", color: "#15803D", label: "✅ Approved" };
    if (status === "rejected")
      return { bg: "#FEE2E2", color: "#DC2626", label: "❌ Rejected" };
    if (status === "pending")
      return { bg: "#FEF9C3", color: "#92400E", label: "⏳ Pending" };
    if (status === "not_uploaded")
      return { bg: "#F3F4F6", color: "#6B7280", label: "○ Not Uploaded" };
    return { bg: "#F3F4F6", color: "#6B7280", label: status };
  };

  const renderDocRow = (doc, coTenantId = null) => {
    const s = getStatusStyle(doc.status);
    const isLoading = actionLoading === doc.ownerDocId;

    return (
      <div
        key={doc.ownerDocId}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: "#fff",
          borderRadius: 8,
          border: "0.5px solid var(--border-light)",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 6,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
          <span>📄</span>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{doc.docName}</span>
          {doc.isOptional && (
            <span
              style={{
                fontSize: 10,
                color: "var(--text-tertiary)",
                background: "var(--bg-secondary)",
                borderRadius: 4,
                padding: "1px 6px",
              }}
            >
              Optional
            </span>
          )}
        </div>

        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: "3px 10px",
            borderRadius: 99,
            background: s.bg,
            color: s.color,
          }}
        >
          {s.label}
        </span>

        <div style={{ display: "flex", gap: 6 }}>
          {doc.fileUrl && (
            <a href={doc.fileUrl} target="_blank" rel="noreferrer">
              <button
                className="btn btn-xs"
                style={{
                  background: "#EFF6FF",
                  color: "#1D4ED8",
                  borderColor: "#BFDBFE",
                }}
              >
                👁 View
              </button>
            </a>
          )}

          {/* not_uploaded — optional ho ya na ho, upload button do */}
          {doc.status === "not_uploaded" && (
            <button
              className="btn btn-xs"
              style={{ background: "var(--teal)", color: "#fff" }}
              onClick={() => triggerUpload(doc.ownerDocId, coTenantId)}
              disabled={isLoading}
            >
              {isLoading ? "Uploading…" : "📤 Upload"}
            </button>
          )}

          {doc.status === "rejected" && (
            <button
              className="btn btn-xs"
              style={{
                background: "#FEE2E2",
                color: "#DC2626",
                borderColor: "#FCA5A5",
              }}
              onClick={() => triggerUpload(doc.ownerDocId, coTenantId)}
              disabled={isLoading}
            >
              {isLoading ? "Uploading…" : "🔄 Re-upload"}
            </button>
          )}

          {doc.status === "pending" && doc.tenantKYCDocId && (
            <button
              className="btn btn-xs"
              style={{
                background: "#FEF2F2",
                color: "#DC2626",
                borderColor: "#FCA5A5",
              }}
              onClick={() => handleDelete(doc.tenantKYCDocId)}
              disabled={isLoading}
            >
              {isLoading ? "Deleting…" : "🗑 Delete"}
            </button>
          )}
        </div>

        {doc.status === "rejected" && doc.rejectionReason && (
          <div
            style={{
              width: "100%",
              fontSize: 11,
              color: "#DC2626",
              background: "#FEF2F2",
              borderRadius: 6,
              padding: "5px 10px",
            }}
          >
            ❗ {doc.rejectionReason}
          </div>
        )}
      </div>
    );
  };

  const renderTenantCard = (tenant, coTenantId = null) => {
    const s = getStatusStyle(tenant.overallStatus);
    return (
      <div
        className="card"
        key={coTenantId ?? "primary"}
        style={{ marginBottom: 16 }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 14,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "var(--teal-light)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
              }}
            >
              {coTenantId ? "👤" : "🧑"}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{tenant.name}</div>
              <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                {coTenantId
                  ? `Co-Tenant · ${tenant.relation}${tenant.age ? `, Age ${tenant.age}` : ""}`
                  : "Primary Tenant · Self"}
              </div>
            </div>
          </div>
          <span
            style={{
              background: s.bg,
              color: s.color,
              borderRadius: 99,
              padding: "5px 14px",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {s.label}
          </span>
        </div>

        {tenant.documents.map((doc) => renderDocRow(doc, coTenantId))}
      </div>
    );
  };

  if (loading)
    return (
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">My Documents</div>
            <div className="page-sub">KYC &amp; identity documents</div>
          </div>
        </div>
        <div className="content">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 80,
                borderRadius: 10,
                marginBottom: 12,
                background:
                  "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.4s infinite",
              }}
            />
          ))}
        </div>
      </div>
    );

  const banner = getBanner(data?.overallStatus);

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">My Documents</div>
          <div className="page-sub">KYC &amp; identity documents</div>
        </div>
      </div>
      <div className="content">
        {/* Single hidden file input for all uploads */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept="image/*,.pdf"
          onChange={handleFileChange}
        />

        {/* Overall banner */}
        <div
          style={{
            background: banner.bg,
            border: `1px solid ${banner.border}`,
            borderRadius: 10,
            padding: "14px 18px",
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginBottom: 16,
          }}
        >
          <span style={{ fontSize: 28 }}>{banner.icon}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: banner.color }}>
              {banner.title}
            </div>
            <div
              style={{
                fontSize: 12,
                color: banner.color,
                opacity: 0.85,
                marginTop: 3,
              }}
            >
              {banner.sub}
            </div>
          </div>
        </div>

        {data?.primaryTenant && renderTenantCard(data.primaryTenant, null)}
        {data?.coTenants?.map((ct) => renderTenantCard(ct, ct.coTenantId))}
      </div>
    </div>
  );
}
